/* ==========================================================================
   Shoppable videos
   One <video> element is reused by every card: opening a card points it at
   that card's source and fills in the attached product, so the page never
   loads more than one video at a time.
   ========================================================================== */

if (!customElements.get('neo-video-shop')) {
  customElements.define(
    'neo-video-shop',
    class NeoVideoShop extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;

        this.cards = Array.from(this.querySelectorAll('[data-vs-card]'));
        this.modal = this.querySelector('[data-vs-modal]');
        this.video = this.querySelector('[data-vs-video]');
        if (!this.cards.length || !this.modal || !this.video) return;

        this.cardVideos = Array.from(this.querySelectorAll('.neo-vs__card-video'));
        this.player = this.querySelector('[data-vs-player]');
        this.muteButton = this.querySelector('[data-vs-mute]');
        this.product = this.querySelector('[data-vs-product]');
        this.prevButton = this.querySelector('[data-vs-prev]');
        this.nextButton = this.querySelector('[data-vs-next]');
        this.index = 0;
        this.lastFocus = null;

        this.bind();
        this.watchCards();
        this.ready = true;
      }

      /**
       * Card videos play on their own, muted. Only the ones on screen run, so
       * a long rail does not leave a dozen decoders going at once.
       */
      watchCards() {
        if (!this.cardVideos.length) return;

        this.cardVideos.forEach((video) => {
          video.muted = true;
          video.setAttribute('playsinline', '');
        });

        const play = (video) => {
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => {});
        };

        if (!('IntersectionObserver' in window)) {
          this.cardVideos.forEach(play);
          return;
        }

        this.cardObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (this.modalOpen) return;
              if (entry.isIntersecting) play(entry.target);
              else entry.target.pause();
            });
          },
          { threshold: 0.2 }
        );

        this.cardVideos.forEach((video) => this.cardObserver.observe(video));
      }

      /** Stops the rail while the player has the visitor's attention. */
      setCardsPlaying(playing) {
        this.cardVideos.forEach((video) => {
          if (playing) {
            const attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      disconnectedCallback() {
        this.close();
        if (this.cardObserver) this.cardObserver.disconnect();
        if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
        this.ready = false;
      }

      bind() {
        this.cards.forEach((card, i) => card.addEventListener('click', () => this.open(i)));

        this.querySelectorAll('[data-vs-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );

        if (this.prevButton) this.prevButton.addEventListener('click', () => this.show(this.index - 1));
        if (this.nextButton) this.nextButton.addEventListener('click', () => this.show(this.index + 1));

        if (this.muteButton) {
          this.muteButton.addEventListener('click', () => {
            this.video.muted = !this.video.muted;
            this.reflectMuted();
          });
        }

        // Autoplay needs muted; once it is running, offer sound.
        this.video.addEventListener('ended', () => this.show(this.index + 1));

        this.bindSwipe();

        this.onKeydown = (event) => {
          if (!this.modal.classList.contains('is-open')) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.show(this.index - 1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.show(this.index + 1);
          } else if (event.key === 'Tab') {
            this.trapFocus(event);
          }
        };
        document.addEventListener('keydown', this.onKeydown);
      }

      /**
       * Swiping the player moves between videos. This is the only way to
       * navigate on mobile, where the arrows are hidden.
       */
      bindSwipe() {
        if (!this.player) return;

        let startX = 0;
        let startY = 0;
        let tracking = false;

        this.player.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) return;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            tracking = true;
          },
          { passive: true }
        );

        this.player.addEventListener(
          'touchend',
          (event) => {
            if (!tracking) return;
            tracking = false;

            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;

            // Horizontal swipe, or a vertical flick as on a reels feed.
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
              this.show(this.index + (dx < 0 ? 1 : -1));
            } else if (Math.abs(dy) > 70 && Math.abs(dy) > Math.abs(dx)) {
              this.show(this.index + (dy < 0 ? 1 : -1));
            }
          },
          { passive: true }
        );

        this.player.addEventListener('touchcancel', () => {
          tracking = false;
        });
      }

      open(index) {
        this.modalOpen = true;
        this.setCardsPlaying(false);
        this.lastFocus = document.activeElement;
        this.modal.classList.add('is-open');
        this.modal.removeAttribute('aria-hidden');
        document.body.classList.add('neo-vs-locked');
        this.show(index);

        const close = this.querySelector('[data-vs-close-button]');
        if (close) window.setTimeout(() => close.focus(), 60);
      }

      close() {
        if (!this.modal || !this.modal.classList.contains('is-open')) return;
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('neo-vs-locked');

        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();

        this.modalOpen = false;
        this.setCardsPlaying(true);

        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      }

      show(index) {
        const total = this.cards.length;
        this.index = ((index % total) + total) % total;

        const card = this.cards[this.index];
        const data = card.dataset;

        this.video.poster = data.vsPoster || '';
        this.video.src = data.vsSrc || '';
        this.video.muted = true;
        this.video.play().catch(() => {
          /* a blocked autoplay just leaves the poster and controls visible */
        });
        this.reflectMuted();

        if (this.product) this.fillProduct(data);

        const single = total < 2;
        if (this.prevButton) this.prevButton.hidden = single;
        if (this.nextButton) this.nextButton.hidden = single;

        if (this.player) {
          this.player.setAttribute('aria-label', `${data.vsTitle || 'Video'} — ${this.index + 1} of ${total}`);
        }
      }

      fillProduct(data) {
        if (!data.vsProductUrl) {
          this.product.hidden = true;
          return;
        }

        this.product.hidden = false;
        this.product.href = data.vsProductUrl;

        const set = (selector, value) => {
          const el = this.product.querySelector(selector);
          if (el) el.textContent = value || '';
        };

        set('[data-vs-product-title]', data.vsProductTitle);
        set('[data-vs-product-price]', data.vsProductPrice);

        const compare = this.product.querySelector('[data-vs-product-compare]');
        if (compare) {
          compare.textContent = data.vsProductCompare || '';
          compare.hidden = !data.vsProductCompare;
        }

        const thumb = this.product.querySelector('[data-vs-product-image]');
        if (thumb) {
          if (data.vsProductImage) {
            thumb.src = data.vsProductImage;
            thumb.hidden = false;
          } else {
            thumb.removeAttribute('src');
            thumb.hidden = true;
          }
        }
      }

      reflectMuted() {
        if (!this.muteButton) return;

        const muted = this.video.muted;
        this.muteButton.setAttribute('aria-pressed', String(muted));
        this.muteButton.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');

        // `hidden` is an HTMLElement property, so `svg.hidden = true` silently
        // does nothing. toggleAttribute is on Element and works for both.
        const mutedIcon = this.muteButton.querySelector('[data-vs-icon-muted]');
        const soundIcon = this.muteButton.querySelector('[data-vs-icon-sound]');
        if (mutedIcon) mutedIcon.toggleAttribute('hidden', !muted);
        if (soundIcon) soundIcon.toggleAttribute('hidden', muted);
      }

      trapFocus(event) {
        const items = Array.from(
          this.modal.querySelectorAll('a[href], button:not([disabled]), video[controls]')
        ).filter((el) => !el.hidden && el.offsetParent !== null);
        if (!items.length) return;

        const first = items[0];
        const last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
